/**
 * Helpers for working with V4 teams alongside the existing V3 team list.
 *
 * The V4 platform exposes its own Team API via lib-ui-core (`teamsList`,
 * `teamsCreate`, `teamsUpdate`, `teamsDelete`, `teamsDevicesCreate`,
 * `teamsDevicesDelete`). Some teams will live only in V4, so the user-facing
 * Teams page needs to render both sources together — we keep the V3 Immutable
 * `Team` model as the single shape the rest of the app consumes and tag each
 * record with a `source` field so views can branch behaviour (save path,
 * device-id space, etc.) when needed.
 *
 * V4 device-link members are surfaced through `team.deviceIds` as V4 device
 * UUIDs (strings). On save, these are reconciled against the previous list via
 * `syncV4TeamDevices` and translated into `teamsDevicesCreate` /
 * `teamsDevicesDelete` calls.
 */

import V4API from '../../api/V4client';
import { mapV4TeamToV3 } from './transforms';

/**
 * Fetch V4 teams and map them to the V3 Team-shaped objects.
 * Returns an empty array on failure — the V3 list must still render.
 *
 * @param {object} [scope] - optional V4 request options (e.g. X-Account-Scope header).
 */
export async function fetchV4Teams(scope) {
    try {
        const res = await V4API.teamsList(scope);
        const items = res?.data || [];
        const detailed = await Promise.all(items.map(async (team) => {
            if (!team?.id) return team;
            try {
                const detail = await V4API.teamsDetail(team.id, scope);
                // Fall back to the list payload if detail came back empty —
                // the team still renders, just without member chips.
                return detail?.data || team;
            } catch (e) {
                return team;
            }
        }));
        return detailed.map(mapV4TeamToV3);
    } catch (e) {
        // Don't block the V3 list when V4 is unreachable / not deployed.
        // eslint-disable-next-line no-console
        console.error('Failed to fetch V4 teams', e?.response?.data || e);
        return [];
    }
}

/**
 * Build the request body accepted by `teamsCreate` / `teamsUpdate`.
 * The V3 `Team` Immutable model is our single UI shape; this helper projects
 * the editable fields down to the V4 wire format.
 */
function toV4TeamRequest(team) {
    return {
        name: team.get ? team.get('name') : team.name,
        description: (team.get ? team.get('description') : team.description) || '',
    };
}

/**
 * Create or update a V4 team from our V3-shaped Team record.
 * Returns the fresh V4 team payload from the server. Pass `scope` as the V4
 * request-options object (e.g. `{ headers: { 'X-Account-Scope': uuid } }`)
 * when the call needs to be account-scoped — the platform infers the team's
 * owner_id from that header on create.
 */
export async function saveV4Team(team, scope) {
    const body = toV4TeamRequest(team);
    const id = team.get ? team.get('id') : team.id;
    if (id) {
        const res = await V4API.teamsUpdate(id, body, scope);
        return res?.data;
    }
    const res = await V4API.teamsCreate(body, scope);
    return res?.data;
}

/**
 * Delete a V4 team by id. No-op when no id is provided.
 */
export async function deleteV4Team(team, scope) {
    const id = team?.get ? team.get('id') : team?.id;
    if (!id) return;
    await V4API.teamsDelete(id, scope);
}

/**
 * Reconcile a V4 team's device-link list by diffing the desired set against
 * the previous one. Devices are referenced by their V4 UUID — for V3-migrated
 * devices the caller is responsible for translating numeric V3 ids to UUIDs
 * before invoking this helper.
 *
 * @param {string} teamId      - V4 team UUID
 * @param {string[]} nextDeviceIds - desired device UUIDs after the edit
 * @param {string[]} prevDeviceIds - device UUIDs currently linked on the server
 * @param {object} [scope]     - optional V4 request-options (X-Account-Scope header)
 */
export async function syncV4TeamDevices(teamId, nextDeviceIds = [], prevDeviceIds = [], scope) {
    if (!teamId) return;

    const nextSet = new Set(nextDeviceIds.filter(Boolean).map(String));
    const prevSet = new Set(prevDeviceIds.filter(Boolean).map(String));

    const toLink = [...nextSet].filter(id => !prevSet.has(id));
    const toUnlink = [...prevSet].filter(id => !nextSet.has(id));

    await Promise.all([
        ...toLink.map(deviceId => V4API.teamsDevicesCreate(teamId, deviceId, scope)),
        ...toUnlink.map(deviceId => V4API.teamsDevicesDelete(teamId, deviceId, scope)),
    ]);
}

/**
 * Returns the list of currently-linked V4 device UUIDs for a V4 team, derived
 * from the cached `v4Data.members` payload that came back with `teamsList`.
 * Used by the saga to compute the diff against the user's edited deviceIds.
 */
export function getV4TeamDeviceIds(team) {
    const members = team?.v4Data?.members || team?.get?.('v4Data')?.members || [];
    return members
        .filter(m => m && m.active !== false && m.device_id)
        .map(m => m.device_id);
}
